import {useContext} from 'react';

import {AccountSelectorContext} from '../../components/Search/SearchNav/AccountSelectorContextProvider';
import {AccountType} from '../../components/Search/SearchNav/types';
import {useTrialCardAccountExpirationQuery} from '../../generated/graphql';
import {
  useGetIsAcademicUser,
  useGetTrialPlanName,
} from '../../pages/TrialEndPage/useGetIsAcademicUser';
import {differenceInDays} from '../date';
import {
  isCommunityEditionPrimarySubscription,
  isTrialPlanPrimarySubscription,
} from './pricing';

export type PlanUpsellState =
  | {
      kind: 'trial';
      isAcademic: boolean;
      trialPlanName: string;
      /**
       * Whole days until the trial expires, for display only.
       * Negative once expired, null when the no expiration.
       */
      daysUntilExpired: number | null;
      isExpired: boolean;
    }
  | {kind: 'free'; daysUntilExpired: number | null};

/**
 * Plan state for upsell surfaces (homepage trial card, side-nav plan item):
 * whether the selected account is on a reverse trial or the free plan, how
 * close the trial is to expiring, and whether the academic flow applies.
 * `state` is null for personal accounts, paid orgs, and while loading.
 */
export function usePlanUpsellState(): {
  loading: boolean;
  state: PlanUpsellState | null;
} {
  const {selectedAccount} = useContext(AccountSelectorContext);
  const skip =
    selectedAccount == null ||
    selectedAccount.accountType === AccountType.Personal;
  const {loading, data} = useTrialCardAccountExpirationQuery({
    variables: {organizationId: selectedAccount?.id ?? ''},
    skip,
  });
  const isAcademic = useGetIsAcademicUser();
  const trialPlanName = useGetTrialPlanName();

  if (skip || loading) {
    return {loading: !skip && loading, state: null};
  }

  const subscriptions = data?.organization?.subscriptions ?? [];
  const daysUntil = (expiresAt: string | null | undefined): number | null =>
    expiresAt != null
      ? differenceInDays(new Date(expiresAt), new Date())
      : null;

  const trialSubscription = subscriptions.find(subscription =>
    isTrialPlanPrimarySubscription(subscription)
  );
  if (trialSubscription != null) {
    const {expiresAt} = trialSubscription;
    return {
      loading: false,
      state: {
        kind: 'trial',
        isAcademic,
        trialPlanName,
        daysUntilExpired: daysUntil(expiresAt),
        isExpired:
          expiresAt != null && new Date(expiresAt).getTime() <= Date.now(),
      },
    };
  }

  const freeSubscription = subscriptions.find(subscription =>
    isCommunityEditionPrimarySubscription(subscription)
  );
  if (freeSubscription != null) {
    return {
      loading: false,
      state: {
        kind: 'free',
        daysUntilExpired: daysUntil(freeSubscription.expiresAt),
      },
    };
  }

  return {loading: false, state: null};
}
